import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useIsMobile from '../hooks/useIsMobile';
import { api, apiFetch } from '../lib/api';
import TravelModal from '../components/TravelModal';
import EventModal from '../components/EventModal';
import LodgingPanel from '../components/LodgingPanel';
import SendModal from '../components/ReviewAndSendModal';
import { getTravelHero } from '../lib/eventHero';
import { Card, PersonBadge, StatusPill, SLabel, Btn, fmt12, fmtDate, todayStr, WeatherIcon } from '../components/ui';
import { localizeEventTime } from '../lib/tz';
import LoadFailure from '../components/LoadFailure';
import AdminTodayEmpty from '../components/AdminTodayEmpty';
import { usePeople } from '../context/PeopleContext';

// Friendly error copy for the LoadFailure block — keeps the failure messaging
// consistent with the iOS NSURLError-aware variant. Browsers don't give us
// the same error codes, so we lean on `TypeError: Failed to fetch` (offline /
// CORS / DNS) and fall back to a generic line.
function friendlyMessage(err) {
  const msg = (err?.message || '').toLowerCase();
  if (msg.includes('failed to fetch') || msg.includes('networkerror')) {
    return "You're offline or the server isn't reachable.";
  }
  if (msg.includes('timeout')) return 'The server took too long to respond.';
  return "Couldn't reach the server.";
}

const KNOWN_ALIASES = { 'paul kevin jonas ii':'Kevin Jonas','paul kevin jonas':'Kevin Jonas','paul jonas':'Kevin Jonas','kevin jonas ii':'Kevin Jonas','kevin jonas':'Kevin Jonas' };
const KNOWN_INITIALS = { 'kevin jonas':'K2','kevin jonas ii':'K2' };
function getPassengerInitials(name) {
  if (!name) return '?';
  const lower = name.toLowerCase().trim();
  const suf = new Set(['ii','iii','iv','v','jr','jr.','sr','sr.']);
  const clean = lower.split(' ').filter(w => !suf.has(w)).join(' ');
  const resolved = KNOWN_ALIASES[lower] || KNOWN_ALIASES[clean] || name;
  const rLower = resolved.toLowerCase();
  if (KNOWN_INITIALS[rLower]) return KNOWN_INITIALS[rLower];
  const parts = resolved.split(' ').filter(p => p && !suf.has(p.toLowerCase()));
  if (parts.length <= 1) return (parts[0]||'?')[0].toUpperCase();
  return ((parts[0][0]||'') + (parts[parts.length-1][0]||'')).toUpperCase();
}

function isEventPast(event, sheetDate) {
  const endTime = event.timeEnd || event.timeStart;
  if (!endTime) return false;
  const now = new Date();
  const [h, m] = endTime.split(':').map(Number);
  const eventEnd = sheetDate ? new Date(sheetDate + 'T00:00:00') : new Date();
  eventEnd.setHours(h, m, 0, 0);
  return now > eventEnd;
}

// Linkify phone numbers and addresses in text
function LinkifiedText({ text, style }) {
  if (!text) return null;
  // Match phone numbers
  const phoneRe = /(\+?1?[\s\-.]?\(?\d{3}\)?[\s\-.]?\d{3}[\s\-.]?\d{4}|\d{10,11})/g;
  const parts = [];
  let last = 0;
  let match;
  while ((match = phoneRe.exec(text)) !== null) {
    if (match.index > last) parts.push({ type:'text', value: text.slice(last, match.index) });
    parts.push({ type:'phone', value: match[0] });
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push({ type:'text', value: text.slice(last) });
  if (parts.length === 0) return <span style={style}>{text}</span>;
  return (
    <span style={style}>
      {parts.map((p, i) => p.type === 'phone' ? (
        <a key={i} href={`tel:${p.value.replace(/\D/g,'')}`} onClick={e => e.stopPropagation()}
          style={{ color:'#22c55e', textDecoration:'none', fontWeight:600 }}>
          <i className="fa-solid fa-phone" style={{ fontSize:10, marginRight:3 }}></i>{p.value}
        </a>
      ) : <span key={i}>{p.value}</span>)}
    </span>
  );
}

function EventCard({ event, sheetDate, onTap, sheetTz }) {
  const { colorMap } = usePeople();
  const past = isEventPast(event, sheetDate);
  const persons = (event.person||'').split(',').map(p=>p.trim()).filter(p => p && p.toLowerCase() !== 'everyone');
  const personColors = persons.map(p => {
    const lower = p.toLowerCase();
    const key = Object.keys(colorMap).find(k => k.toLowerCase() === lower)
      || Object.keys(colorMap).find(k => k.toLowerCase().split(' ')[0] === lower.split(' ')[0]);
    return key ? colorMap[key] : null;
  }).filter(Boolean);
  const pc = personColors[0] || '#e6edf3';
  const isColored = personColors.length > 0;
  const barStyle = personColors.length > 1
    ? { background: `linear-gradient(to bottom, ${personColors.join(', ')})` }
    : { background: isColored ? pc : 'var(--border2)' };

  return (
    <div onClick={() => onTap && onTap(event)}
      style={{ background:'var(--card)', borderRadius:12, overflow:'hidden', border:'1px solid var(--border)', marginBottom:8, cursor:'pointer', opacity: past ? 0.4 : 1, transition:'opacity 0.3s' }}>
      <div style={{ display:'flex', alignItems:'stretch' }}>
        <div style={{ width:4, flexShrink:0, ...barStyle }} />
        <div style={{ flex:1, padding:'12px 16px' }}>
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:8 }}>
            <p style={{ fontSize:16, fontWeight:800, color: isColored ? pc : 'var(--text)', margin:0, lineHeight:1.4 }}>
              {event.timeStart
                ? (() => {
                    const { localTime, sourceLabel } = localizeEventTime(event, sheetTz, fmt12);
                    return (
                      <>
                        <span style={{ textDecoration:'underline' }}>{fmt12(localTime || event.timeStart)}</span>
                        {sourceLabel && <span style={{ fontSize:10, fontWeight:700, color:'#b45309', background:'rgba(245,158,11,0.15)', border:'1px solid rgba(245,158,11,0.4)', borderRadius:8, padding:'1px 6px', marginLeft:6, whiteSpace:'nowrap' }}>{sourceLabel}</span>}
                        <span>: {event.title}</span>
                      </>
                    );
                  })()
                : event.title}
            </p>
            <div style={{ display:'flex', alignItems:'center', gap:4, flexShrink:0 }}>
              {persons.length > 1 && (
                <span style={{ fontSize:11, fontWeight:700, color: pc, background: pc+'22', border:'1px solid '+pc+'44', borderRadius:20, padding:'1px 7px' }}>
                  +{persons.length}
                </span>
              )}
              {event.files?.length > 0 && <i className="fa-solid fa-paperclip" style={{ fontSize:11, color:'var(--text3)' }}></i>}
            </div>
          </div>
          {event.location && (
            <a href={`https://maps.apple.com/?q=${encodeURIComponent(event.location)}`} onClick={e => e.stopPropagation()}
              style={{ fontSize:12, color:'var(--blue)', textDecoration:'none', display:'block', marginTop:4 }}>
              <i className="fa-solid fa-location-dot" style={{ fontSize:10, marginRight:4 }}></i>{event.location}
            </a>
          )}
          {event.notes && <LinkifiedText text={event.notes.split('\n')[0]} style={{ fontSize:11, color:'var(--text3)', marginTop:4, lineHeight:1.5, display:'block' }} />}
        </div>
      </div>
    </div>
  );
}


export default function AdminToday() {
  const navigate = useNavigate();
  const { colorMap } = usePeople();
  const [travelModal, setTravelModal] = useState(null);
  const [eventModal,  setEventModal]  = useState(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState(new Set());
  const [sending, setSending] = useState(false);
  const isMobile = useIsMobile();
  const [artists,   setArtists]   = useState([]);
  const [sheets,    setSheets]    = useState([]);
  const [fullSheet, setFullSheet] = useState(null);
  const [loadingSheet, setLoadingSheet] = useState(false);
  const [selArtist, setSelArtist] = useState(null);
  const [showAll,   setShowAll]   = useState(false);
  const [allSheets, setAllSheets] = useState([]);
  const [searchParams] = useSearchParams();
  const [selDate,   setSelDate]   = useState(searchParams.get('date') || todayStr());
  const [loading,   setLoading]   = useState(true);
  // Surfaces a friendly retry block when the artists call fails — usually
  // a network blip on a phone-as-hotspot. Cleared on successful retry.
  const [artistsError, setArtistsError] = useState(null);
  const today = todayStr();

  function loadArtists() {
    setLoading(true);
    setArtistsError(null);
    return api.getArtists().then(d => {
      setArtists(d.artists || []);
      if (d.artists?.length) {
        const paramArtist = searchParams.get('artist');
        const match = paramArtist && d.artists.find(a => a.id === paramArtist);
        setSelArtist(match ? match.id : d.artists[0].id);
      }
    }).catch(err => {
      setArtists([]);
      setArtistsError(friendlyMessage(err));
    }).finally(() => setLoading(false));
  }

  useEffect(() => { loadArtists(); }, []);

  useEffect(() => {
    if (!selArtist) return;
    api.getSheets(selArtist).then(d => setSheets(d.sheets || [])).catch(() => setSheets([]));
  }, [selArtist]);

  // Fetch all artists' sheets when showAll
  useEffect(() => {
    if (!showAll || !artists.length || !selDate) return;
    setLoadingSheet(true);
    Promise.all(artists.map(async a => {
      try {
        const d = await api.getSheets(a.id);
        const published = (d.sheets || []).filter(s => s.status === 'published');
        const dateSheet = published.find(s => s.date === selDate);
        if (!dateSheet) return null;
        const full = await apiFetch('/sheets/' + dateSheet.id);
        return { ...full, artistInfo: a };
      } catch { return null; }
    })).then(results => {
      setAllSheets(results.filter(Boolean));
      setLoadingSheet(false);
    });
  }, [showAll, artists, selDate]);

  // Load full sheet with events when date or sheets change
  useEffect(() => {
    if (!sheets.length || !selDate) { setFullSheet(null); return; }
    const match = sheets.find(s => s.date === selDate && s.status === 'published')
                || sheets.find(s => s.date === selDate);
    if (!match) { setFullSheet(null); return; }
    setLoadingSheet(true);
    apiFetch(`/sheets/${match.id}`)
      .then(data => {
        const evts = (data.events || []).sort((a, b) => {
          if (a.nextDay && !b.nextDay) return 1;
          if (!a.nextDay && b.nextDay) return -1;
          if (!a.timeStart && !b.timeStart) return 0;
          if (!a.timeStart) return 1;
          if (!b.timeStart) return -1;
          return a.timeStart.localeCompare(b.timeStart);
        });
        setFullSheet({ ...data, events: evts });
      })
      .catch(() => setFullSheet(null))
      .finally(() => setLoadingSheet(false));
  }, [selDate, sheets]);

  const weekDates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(selDate + 'T12:00:00');
    d.setDate(d.getDate() - 3 + i);
    return d.toISOString().split('T')[0];
  });

  const _upcomingSheets = sheets
    .filter(s => s.date >= today && s.status === 'published')
    .sort((a,b) => a.date.localeCompare(b.date))
    .slice(0, 5);

  const artistObj = artists.find(a => a.id === selArtist);

  if (loading) return <div style={{ padding:40, color:'var(--text2)' }}>Loading...</div>;
  if (artistsError && !artists.length) {
    return (
      <div style={{ padding:'24px 16px' }}>
        <LoadFailure
          fullscreen
          title="Couldn't load your tours"
          message={artistsError}
          onRetry={loadArtists}
        />
      </div>
    );
  }

  // Onboarding: no artists — prompt to create one
  if (!artists.length) {
    return (
      <div style={{ padding: isMobile ? '40px 20px' : '80px 40px', maxWidth:500, margin:'0 auto', textAlign:'center' }}>
        <div style={{ width:80, height:80, borderRadius:20, background:'linear-gradient(135deg,#1E40AF,#1E40AF)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 24px' }}>
          <i className="fa-solid fa-music" style={{ fontSize:32, color:'#fff' }}></i>
        </div>
        <h1 style={{ fontSize:28, fontWeight:900, color:'var(--text)', marginBottom:8 }}>Welcome to Day Sheets</h1>
        <p style={{ fontSize:15, color:'var(--text2)', marginBottom:32, lineHeight:1.6 }}>
          Get started by creating your first artist profile. You'll be able to build day sheets, manage travel, and distribute schedules to your team.
        </p>
        <button onClick={() => navigate('/admin/artists?action=new')}
          style={{ padding:'16px 32px', borderRadius:12, background:'linear-gradient(135deg,#1E40AF,#1E40AF)', border:'none', color:'#fff', fontFamily:'inherit', fontSize:16, fontWeight:800, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:10, boxShadow:'0 4px 16px rgba(30,64,175,0.3)' }}>
          <i className="fa-solid fa-plus" style={{ fontSize:14 }}></i> Create Your First Artist
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? '16px 12px' : '32px 40px', maxWidth:1100, margin:'0 auto' }}>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: isMobile ? 16 : 32 }}>
        <div>
          <h1 style={{ fontSize: isMobile ? 24 : 32, fontWeight:900, letterSpacing:-0.5, color:'var(--text)' }}>
            {new Date(selDate+'T12:00:00').toLocaleDateString('en-US',{month:'long',day:'numeric'})}
          </h1>
          <p style={{ fontSize: isMobile ? 12 : 14, color:'var(--text2)', marginTop:4 }}>
            {fmtDate(selDate,{weekday:'long',month:'long',day:'numeric',year:'numeric'})}
          </p>
        </div>
        {/* Auto generate button — hidden but code preserved
        {artistObj && !showAll && (
          <button onClick={async () => {
            const newVal = !artistObj.autoDaySheet;
            try {
              await apiFetch(`/artists/${artistObj.id}`, { method:'PATCH', body: JSON.stringify({ autoDaySheet: newVal }) });
              setArtists(prev => prev.map(a => a.id === artistObj.id ? { ...a, autoDaySheet: newVal } : a));
            } catch {}
          }}
            style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 14px', borderRadius:10,
              background: artistObj.autoDaySheet ? 'rgba(63,185,80,0.1)' : 'var(--bg)',
              border: `1.5px solid ${artistObj.autoDaySheet ? 'rgba(63,185,80,0.3)' : 'var(--border)'}`,
              cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:700,
              color: artistObj.autoDaySheet ? '#22c55e' : 'var(--text3)' }}>
            <div style={{ width:8, height:8, borderRadius:'50%', background: artistObj.autoDaySheet ? '#22c55e' : 'var(--border)' }} />
            Auto
          </button>
        )}
        */}
      </div>

      <div style={{ maxWidth:700 }}>
        <div>
          {/* Artist selector + actions */}
          <div style={{ display:'flex', gap: isMobile ? 10 : 14, marginBottom: isMobile ? 16 : 24, flexWrap:'wrap', alignItems:'center', justifyContent: isMobile ? 'center' : 'flex-start' }}>
            {artists.length > 1 && (
              <button onClick={() => setShowAll(!showAll)}
                style={{ display:'flex', flexDirection:'column', alignItems:'center', gap: isMobile ? 4 : 6, padding:0, background:'none', border:'none', cursor:'pointer', fontFamily:'inherit', transition:'all 0.15s', opacity: showAll ? 1 : 0.6 }}>
                <div style={{ width:60, height:60, borderRadius:'50%', border:`2.5px solid ${showAll ? 'var(--blue)' : 'var(--border)'}`,
                  display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, fontWeight:800, color: showAll ? 'var(--blue)' : 'var(--text3)',
                  background: showAll ? 'rgba(79,156,249,0.1)' : 'var(--card)',
                  boxShadow: showAll ? '0 0 0 3px rgba(79,156,249,0.25)' : 'none', transition:'all 0.15s' }}>
                  All
                </div>
                <span style={{ fontSize:11, fontWeight:700, color: showAll ? 'var(--blue)' : 'var(--text2)' }}>All</span>
              </button>
            )}
            {artists.map(a => {
              const isSel = !showAll && selArtist===a.id;
              const hasSel = sheets.some(s => s.artistId === a.id && s.date === selDate);
              return (
                <button key={a.id} onClick={() => { setShowAll(false); setSelArtist(a.id); }}
                  style={{ display:'flex', flexDirection:'column', alignItems:'center', gap: isMobile ? 4 : 6, padding:0, background:'none', border:'none', cursor:'pointer', fontFamily:'inherit', transition:'all 0.15s', opacity: isSel ? 1 : 0.6 }}>
                  <div style={{ width:60, height:60, borderRadius:'50%', border:`2.5px solid ${isSel ? 'var(--blue)' : 'var(--border)'}`,
                    display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, overflow:'hidden',
                    background: a.photo ? 'none' : `${a.color||'var(--card)'}15`,
                    boxShadow: isSel ? '0 0 0 3px rgba(79,156,249,0.25)' : 'none', transition:'all 0.15s', position:'relative' }}>
                    {a.photo
                      ? <img src={a.photo} alt={a.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                      : (a.emoji||'🎵')}
                    {hasSel && <span style={{ position:'absolute', bottom:0, right:0, width:10, height:10, borderRadius:'50%', background:'var(--green)', border:'2px solid var(--card)' }}/>}
                  </div>
                  <span style={{ fontSize:11, fontWeight:700, color: isSel ? 'var(--blue)' : 'var(--text2)', maxWidth:64, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.name?.split(' ')[0]}</span>
                </button>
              );
            })}
          </div>

          {/* Action bar — full on desktop, minimal on mobile */}
          {isMobile ? (
            fullSheet && (
              <div style={{ display:'flex', gap:8, marginBottom:12 }}>
                <Btn variant="ghost" size="sm" onClick={() => navigate(`/admin/sheets/${fullSheet.id}/edit`)} style={{ flex:1 }}><i className="fa-solid fa-pen" style={{ fontSize:10, marginRight:4 }}></i>Edit</Btn>
                {fullSheet.status !== 'published' ? (
                  <Btn variant="success" size="sm" onClick={async () => { await api.publishSheet(fullSheet.id); setFullSheet(s=>({...s,status:'published'})); setSheets(prev=>prev.map(s=>s.id===fullSheet.id?{...s,status:'published'}:s)); }} style={{ flex:1 }}><i className="fa-solid fa-rocket" style={{ fontSize:10, marginRight:4 }}></i>Publish</Btn>
                ) : (
                  <Btn variant="success" size="sm" onClick={() => {
                    const allIds = new Set((fullSheet.artist?.distributionGroups || []).map(g => g.id));
                    allIds.add('ungrouped');
                    setSelectedGroups(allIds);
                    setShowSendModal(true);
                  }} style={{ flex:1 }}><i className="fa-solid fa-envelope" style={{ fontSize:10, marginRight:4 }}></i>Email Send</Btn>
                )}
              </div>
            )
          ) : (
            fullSheet && (
            <div style={{ display:'flex', gap:6, marginBottom:20, flexWrap:'wrap', alignItems:'center' }}>
              <Btn variant="ghost" size="sm" onClick={() => navigate(`/admin/sheets/${fullSheet.id}/edit`)}><i className="fa-solid fa-pen" style={{ fontSize:10, marginRight:4 }}></i>Edit</Btn>
              {fullSheet.status !== 'published' && (
                <Btn variant="success" size="sm" onClick={async () => { await api.publishSheet(fullSheet.id); setFullSheet(s=>({...s,status:'published'})); setSheets(prev=>prev.map(s=>s.id===fullSheet.id?{...s,status:'published'}:s)); }}><i className="fa-solid fa-rocket" style={{ fontSize:10, marginRight:4 }}></i>Publish</Btn>
              )}
              <Btn variant="ghost" size="sm" onClick={() => {
                const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
                window.open(`${API}/email/preview/${fullSheet.id}?token=${localStorage.getItem('ds_token')}`, '_blank');
              }}><i className="fa-regular fa-envelope" style={{ fontSize:10, marginRight:4 }}></i>Preview</Btn>
              <Btn variant="ghost" size="sm" onClick={() => {
                const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
                window.open(`${API}/email/pdf/${fullSheet.id}?token=${localStorage.getItem('ds_token')}`, '_blank');
              }}><i className="fa-solid fa-file-pdf" style={{ fontSize:10, marginRight:4 }}></i>PDF</Btn>
              {fullSheet.status === 'published' && (
                <Btn variant="success" size="sm" onClick={() => {
                  const allIds = new Set((fullSheet.artist?.distributionGroups || []).map(g => g.id));
                  allIds.add('ungrouped');
                  setSelectedGroups(allIds);
                  setShowSendModal(true);
                }}>
                  <i className="fa-solid fa-envelope" style={{ fontSize:10 }}></i> Email Send
                  </Btn>
                )}
            </div>
            )
          )}

          {/* Week strip */}
          <div style={{ display:'flex', gap: isMobile ? 3 : 4, marginBottom: isMobile ? 16 : 28 }}>
            {weekDates.map(d => {
              const isSel   = d === selDate;
              const isToday = d === today;
              const isPast  = d < today;
              const hasSheet = sheets.some(s => s.date === d);
              const dt = new Date(d+'T12:00:00');
              return (
                <button key={d} onClick={() => setSelDate(d)}
                  style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap: isMobile ? 3 : 6,
                    padding: isMobile ? '8px 2px' : '10px 4px', borderRadius: isMobile ? 10 : 12, cursor:'pointer', fontFamily:'inherit',
                    background: isSel ? 'var(--blue)' : isToday ? 'rgba(79,156,249,0.1)' : 'var(--card)',
                    border: `1.5px solid ${isSel ? 'var(--blue)' : isToday ? 'rgba(79,156,249,0.5)' : 'var(--border)'}`,
                    transition:'all 0.15s' }}>
                  <span style={{ fontSize:11, fontWeight:600, color:isSel?'rgba(255,255,255,0.8)':'var(--text3)' }}>
                    {['Su','Mo','Tu','We','Th','Fr','Sa'][dt.getDay()]}
                  </span>
                  <span style={{ fontSize: isMobile ? 14 : 16, fontWeight:800, color:isSel?'white':isPast?'var(--text3)':'var(--text)' }}>
                    {dt.getDate()}
                  </span>
                  {hasSheet && !isSel && <span style={{ width:5, height:5, borderRadius:'50%', background:'var(--blue)' }}/>}
                </button>
              );
            })}
          </div>

          {/* Status badge — outside card */}
          {fullSheet && (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6, marginBottom:10 }}>
              {fullSheet.status === 'published' ? (<>
                <span style={{ display:'inline-flex', alignItems:'center', gap:5,
                  background:'rgba(63,185,80,0.12)', border:'1px solid rgba(63,185,80,0.35)',
                  borderRadius:20, padding:'4px 10px', fontSize:11, fontWeight:700, color:'#3fb950' }}>
                  <span style={{ width:6, height:6, borderRadius:'50%', background:'#3fb950', display:'inline-block', animation:'pulse 2s ease-in-out infinite' }}/>
                  Live
                </span>
                <button onClick={async () => { await api.unpublishSheet(fullSheet.id); setFullSheet(s=>({...s,status:'draft'})); setSheets(prev=>prev.map(s=>s.id===fullSheet.id?{...s,status:'draft'}:s)); }}
                  style={{ background:'none', border:'1px solid var(--border)', borderRadius:20, padding:'3px 8px',
                    cursor:'pointer', fontFamily:'inherit', fontSize:10, fontWeight:600, color:'var(--text3)', transition:'all 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor='var(--red)'; e.currentTarget.style.color='var(--red)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.color='var(--text3)'; }}>
                  Unpublish
                </button>
              </>) : (
                <span style={{ display:'inline-flex', alignItems:'center', gap:5,
                  background:'rgba(210,153,34,0.1)', border:'1px solid rgba(210,153,34,0.3)',
                  borderRadius:20, padding:'4px 10px', fontSize:11, fontWeight:700, color:'#d2991a' }}>
                  <span style={{ width:6, height:6, borderRadius:'50%', background:'#d2991a', display:'inline-block' }}/>
                  Draft
                </span>
              )}
            </div>
          )}

          {/* Day sheet content */}
          {loadingSheet ? (
            <div style={{ textAlign:'center', padding:'48px 0', color:'var(--text3)' }}>Loading…</div>
          ) : showAll ? (
            /* ── All Artists merged view ── */
            (() => {
              if (!allSheets.length) return (
                <div style={{ textAlign:'center', padding:'60px 24px' }}>
                  <p style={{ fontSize:16, fontWeight:700, color:'var(--text)', marginBottom:6 }}>No published sheets</p>
                  <p style={{ fontSize:13, color:'var(--text3)' }}>No artists have published sheets for this date</p>
                </div>
              );
              const mergedEvents = [];
              const notes = [];
              const cities = [];
              allSheets.forEach(s => {
                const a = s.artistInfo || s.artist;
                if (s.notes) notes.push({ artist: a, notes: s.notes });
                if (s.city) cities.push(s.city.replace(', USA',''));
                (s.events||[]).forEach(e => mergedEvents.push({ ...e, _artistColor: a?.color, artistName: a?.name }));
              });
              mergedEvents.sort((a,b) => (a.timeStart||'99:99').localeCompare(b.timeStart||'99:99'));
              return (
                <Card style={{ padding: isMobile ? '16px 12px' : '24px 20px', borderRadius: isMobile ? 16 : 20, overflow:'hidden' }}>
                  <div style={{ textAlign:'center', marginBottom:16 }}>
                    <p style={{ fontSize: isMobile ? 18 : 20, fontWeight:900, color:'var(--text)', marginBottom:2 }}>Combined Day Sheet</p>
                    <p style={{ fontSize:15, fontWeight:500, color:'var(--text2)', marginBottom:8 }}>
                      {new Date(selDate+'T12:00:00').toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric',year:'numeric'})}
                    </p>
                    {cities.length > 0 && <p style={{ fontSize:13, color:'var(--text2)', marginBottom:8 }}>{[...new Set(cities)].join(' / ')}</p>}
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:10, marginBottom:8 }}>
                      {allSheets.map(s => {
                        const a = s.artistInfo || s.artist;
                        return (
                          <span key={s.id} style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:11, fontWeight:700, color: a?.color || 'var(--text3)' }}>
                            <span style={{ width:8, height:8, borderRadius:'50%', background: a?.color || 'var(--text3)' }} />
                            {a?.name?.split(' ')[0]}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                  {notes.map((n, i) => (
                    <div key={i} style={{ background:'rgba(210,153,34,0.1)', border:'1px solid rgba(210,153,34,0.3)', borderLeft:`3px solid ${n.artist?.color || '#d2991a'}`, borderRadius:10, padding:'10px 14px', marginBottom:8 }}>
                      <p style={{ fontSize:11, fontWeight:700, letterSpacing:'1px', color: n.artist?.color || '#d2991a', marginBottom:3 }}>{n.artist?.name}</p>
                      <p style={{ fontSize:13, color:'var(--text2)', lineHeight:1.5 }}>{n.notes}</p>
                    </div>
                  ))}
                  <LodgingPanel hotels={allSheets.flatMap(s => s.hotels || [])} />
                  <div style={{ background:'var(--card)', borderRadius:16, border:'1px solid var(--border)', overflow:'hidden', marginTop:notes.length ? 16 : 0 }}>
                    <div style={{ padding:'14px 16px 10px', borderBottom:'1px solid var(--border)' }}>
                      <p style={{ fontSize:11, fontWeight:700, letterSpacing:'1.5px', color:'var(--text3)', margin:0 }}>SCHEDULE</p>
                    </div>
                    <div style={{ padding:'10px 12px' }}>
                      {mergedEvents.length === 0
                        ? <p style={{ color:'var(--text3)', textAlign:'center', padding:'20px 0', fontSize:14 }}>No events</p>
                        : mergedEvents.map(e => (
                          <div key={e.id} style={{ background:'var(--card)', borderRadius:12, overflow:'hidden', border:'1px solid var(--border)', marginBottom:8 }}>
                            <div style={{ display:'flex', alignItems:'stretch' }}>
                              <div style={{ width:4, flexShrink:0, background: e._artistColor || 'var(--border2)' }} />
                              <div style={{ flex:1, padding:'14px 16px' }}>
                                <p style={{ fontSize:16, fontWeight:800, color: e._artistColor || 'var(--text)', margin:0, lineHeight:1.4 }}>
                                  {e.timeStart ? <><span style={{ textDecoration:'underline' }}>{fmt12(e.timeStart)}</span>: {e.title}</> : e.title}
                                </p>
                                {e.artistName && <p style={{ fontSize:11, fontWeight:600, color: e._artistColor, marginTop:2 }}>{e.artistName}</p>}
                                {e.location && <p style={{ fontSize:12, color:'var(--text3)', marginTop:2 }}><i className="fa-solid fa-location-dot" style={{ fontSize:9, marginRight:4 }}></i>{e.location}</p>}
                              </div>
                            </div>
                          </div>
                        ))
                      }
                    </div>
                  </div>
                  {/* Combined actions */}
                  <div style={{ display:'flex', gap:6, marginTop:16, justifyContent:'center' }}>
                    <Btn variant="ghost" size="sm" onClick={() => {
                      const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
                      const ids = allSheets.map(s => s.id).join(',');
                      window.open(`${API}/email/combined-preview?sheetIds=${ids}&token=${localStorage.getItem('ds_token')}`, '_blank');
                    }}><i className="fa-regular fa-envelope" style={{ fontSize:10, marginRight:4 }}></i>Combined Preview</Btn>
                    <Btn variant="ghost" size="sm" onClick={() => {
                      const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
                      const ids = allSheets.map(s => s.id).join(',');
                      window.open(`${API}/email/combined-pdf?sheetIds=${ids}&token=${localStorage.getItem('ds_token')}`, '_blank');
                    }}><i className="fa-solid fa-file-pdf" style={{ fontSize:10, marginRight:4 }}></i>Combined PDF</Btn>
                  </div>
                </Card>
              );
            })()
          ) : fullSheet ? (
            <Card style={{ padding: isMobile ? '16px 12px' : '24px 20px', borderRadius: isMobile ? 16 : 20, overflow:'hidden' }}>
              {/* Viewer-style centered header */}
              <div style={{ textAlign:'center', marginBottom:16 }}>
                {artistObj?.name && (
                  <p style={{ fontSize: isMobile ? 18 : 20, fontWeight:900, color:'var(--text)', marginBottom:2 }}>
                    {artistObj.name} Day Sheet
                  </p>
                )}
                <p style={{ fontSize:15, fontWeight:500, color:'var(--text2)', marginBottom:4 }}>
                  {new Date(fullSheet.date+'T12:00:00').toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric',year:'numeric'})}
                </p>
                {fullSheet.city && (
                  <p style={{ fontSize:13, color:'var(--text2)', marginBottom:4 }}>{fullSheet.city.replace(', USA','')}</p>
                )}
                {(() => {
                  const weathers = [];
                  if (fullSheet.weather) weathers.push({ location: fullSheet.city ? fullSheet.city.replace(', USA','').split(',')[0].trim() : null, weather: fullSheet.weather });
                  (fullSheet.locationWeathers||[]).forEach(lw => weathers.push(lw));
                  if (!weathers.length) return null;
                  return (
                    <p style={{ fontSize:13, color:'var(--text2)', marginBottom:8, display:'flex', alignItems:'center', justifyContent:'center', gap:6, flexWrap:'wrap' }}>
                      {weathers.map((lw, i) => (
                        <span key={i} style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
                          {i > 0 && <span style={{ color:'var(--text3)', margin:'0 4px' }}>/</span>}
                          <WeatherIcon condition={lw.weather.condition} />
                          <span>{lw.weather.tempHigh}°/{lw.weather.tempLow}°</span>
                          {lw.location && <span style={{ color:'var(--text3)', fontSize:12 }}>{lw.location}</span>}
                        </span>
                      ))}
                    </p>
                  );
                })()}
              </div>


              {/* Note */}
              {/* Travel quick buttons — same as Viewer */}
              {(fullSheet.travel||[]).length > 0 && (() => {
                const typeIcons = { flight:'fa-plane', hotel:'fa-hotel', carservice:'fa-car-side', car:'fa-car', ground:'fa-van-shuttle', train:'fa-train' };
                const typeColors = { flight:'#1E40AF', hotel:'#06B6D4', carservice:'#06B6D4', car:'#fbbf24', ground:'#f472b6', train:'#a78bfa' };
                const resolveColor = (name) => {
                  if (!name) return null;
                  const key = Object.keys(colorMap).find(k => k.toLowerCase() === name.toLowerCase());
                  return key ? colorMap[key] : null;
                };
                // Group by type + passenger
                const groups = {};
                (fullSheet.travel||[]).forEach(r => {
                  const names = (r.passenger||'').split(',').map(s=>s.trim().toLowerCase()).filter(Boolean).sort().join(',');
                  const key = `${r.type}::${names}`;
                  if (!groups[key]) groups[key] = [];
                  groups[key].push(r);
                });
                // Build summary text per travel type
                const getSummary = (r) => {
                  if (r.type === 'flight') return `${r.departureAirport||'?'} → ${r.arrivalAirport||'?'}`;
                  if (r.type === 'hotel') return (r.hotelName||'Hotel').split(' ').slice(0,3).join(' ');
                  if (r.type === 'carservice') return [r.carServicePickup, r.carServiceDropoff].filter(Boolean).map(s=>s.split(',')[0].split(' ').slice(0,2).join(' ')).join(' → ') || 'Car Service';
                  if (r.type === 'ground') return [r.pickupLocation, r.dropoffLocation].filter(Boolean).map(s=>s.split(',')[0].split(' ').slice(0,2).join(' ')).join(' → ') || 'Ground';
                  if (r.type === 'car') return r.carCompany || 'Car Rental';
                  if (r.type === 'train') return `${r.trainFrom||'?'} → ${r.trainTo||'?'}`;
                  return '';
                };
                return (
                  <div style={{ display:'flex', justifyContent:'center', gap: isMobile ? 6 : 8, marginBottom: isMobile ? 10 : 16, flexWrap:'wrap' }}>
                    {Object.values(groups).map(recs => {
                      const r = recs[0];
                      const count = recs.length;
                      const names = (r.passenger||'').split(',').map(s=>s.trim()).filter(Boolean);
                      const colors = names.map(n => resolveColor(n)).filter(Boolean);
                      const c = colors[0] || typeColors[r.type] || '#1E40AF';
                      const summary = count > 1 ? `${count} ${r.type === 'flight' ? 'flights' : 'records'}` : getSummary(r);
                      return (
                        <button key={r.id} onClick={() => setTravelModal(count > 1 ? recs : r)}
                          style={{ minWidth: isMobile ? 72 : 90, padding: isMobile ? '8px 6px' : '10px 12px', borderRadius: isMobile ? 12 : 14, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap: isMobile ? 3 : 4,
                            background: colors.length > 1
                              ? `linear-gradient(135deg, ${colors[0]}22, ${colors[1]}22)`
                              : `${c}14`,
                            border: `1.5px solid ${c}33`,
                            cursor:'pointer', transition:'all 0.15s', position:'relative', fontFamily:'inherit' }}>
                          {count > 1 && (
                            <div style={{ position:'absolute', top:-6, right:-6, minWidth:18, height:18, borderRadius:9,
                              background:c, display:'flex', alignItems:'center', justifyContent:'center', padding:'0 4px',
                              boxShadow:'0 2px 6px rgba(0,0,0,0.3)' }}>
                              <span style={{ fontSize:10, fontWeight:800, color:'#fff', lineHeight:1 }}>{count}</span>
                            </div>
                          )}
                          <i className={`fa-solid ${typeIcons[r.type] || 'fa-circle'}`} style={{ fontSize:20, color:c }}></i>
                          {summary && (
                            <span style={{ fontSize:9, fontWeight:800, color:c, letterSpacing:0.3, lineHeight:1, maxWidth:100, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                              {summary}
                            </span>
                          )}
                          {names.length > 0 && (
                            <div style={{ display:'flex', gap:2, alignItems:'center' }}>
                              {names.map((n, i) => {
                                const nc = resolveColor(n) || 'var(--text3)';
                                return <span key={i} style={{ fontSize:7, fontWeight:800, color: nc, letterSpacing:0.3, lineHeight:1 }}>
                                  {getPassengerInitials(n)}{i < names.length-1 ? ' · ' : ''}
                                </span>;
                              })}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })()}

              {fullSheet.notes && (
                <div style={{ background:'rgba(210,153,34,0.1)', border:'1px solid rgba(210,153,34,0.3)', borderLeft:'3px solid #d2991a', borderRadius:10, padding:'10px 14px', marginBottom:16 }}>
                  <p style={{ fontSize:11, fontWeight:700, letterSpacing:'1px', color:'#d2991a', marginBottom:3 }}><i className="fa-solid fa-thumbtack" style={{ fontSize:11, marginRight:4 }}></i> NOTE</p>
                  <p style={{ fontSize:13, color:'var(--text2)', lineHeight:1.5 }}>{fullSheet.notes}</p>
                </div>
              )}

              <LodgingPanel hotels={fullSheet.hotels || []} />

              {/* Events */}
              <div style={{ marginBottom:16 }}>
                {fullSheet.events.length === 0
                  ? <AdminTodayEmpty
                      artistId={selArtist}
                      date={selDate}
                      hideCalendarTile={!!artists.find(a => a.id === selArtist)?.googleTokens}
                    />
                  : fullSheet.events.map((e, i) => (
                      <React.Fragment key={e.id}>
                        {e.nextDay && (i === 0 || !fullSheet.events[i-1].nextDay) && (
                          <div style={{ display:'flex', alignItems:'center', gap:10, margin:'12px 0', padding:'0 4px' }}>
                            <div style={{ flex:1, height:1, background:'linear-gradient(to right, var(--border), transparent)' }} />
                            <span style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, color:'var(--text3)', whiteSpace:'nowrap' }}>NEXT DAY</span>
                            <div style={{ flex:1, height:1, background:'linear-gradient(to left, var(--border), transparent)' }} />
                          </div>
                        )}
                        <EventCard event={e} sheetDate={fullSheet?.date} sheetTz={fullSheet?.timezone} onTap={(ev) => {
                          const sorted = [...(fullSheet.events||[])].sort((a,b) => (a.timeStart||'99:99').localeCompare(b.timeStart||'99:99'));
                          const idx = sorted.findIndex(x => x.id === ev.id);
                          setEventModal({ event: ev, sheet: fullSheet, prevEvent: idx > 0 ? sorted[idx-1] : null, nextEvent: idx < sorted.length-1 ? sorted[idx+1] : null });
                        }} />
                      </React.Fragment>
                    ))
                }
              </div>

              {/* Travel detail cards */}
              {(fullSheet.travel||[]).length > 0 && (
                <div style={{ background:'var(--card)', borderRadius:16, border:'1px solid var(--border)', overflow:'hidden', marginTop:16 }}>
                  <div style={{ padding:'14px 16px 10px', borderBottom:'1px solid var(--border)' }}>
                    <p style={{ fontSize:11, fontWeight:700, letterSpacing:'1.5px', color:'var(--text3)', margin:0 }}>
                      <i className="fa-solid fa-plane" style={{ marginRight:5, fontSize:10 }}></i>TRAVEL
                    </p>
                  </div>
                  <div style={{ padding:'10px 12px', display:'flex', flexDirection:'column', gap:8 }}>
                    {(fullSheet.travel||[]).map(r => {
                      const hero = getTravelHero(r);
                      const icons = { flight:'fa-plane', hotel:'fa-hotel', carservice:'fa-car-side', car:'fa-car', ground:'fa-van-shuttle', train:'fa-train' };
                      const typeLabel = { flight:'Flight', hotel:'Hotel', carservice:'Car Service', car:'Car Rental', ground:'Ground', train:'Train' }[r.type] || r.type;
                      const name = r.type==='flight' ? `${r.airline||''} ${r.flightNumber||''}`.trim()
                        : r.type==='carservice' ? r.carServiceCompany||'Car Service'
                        : r.hotelName||r.carCompany||r.driverName||r.trainCompany||r.type;
                      const detail = r.type==='flight' ? `${r.departureAirport||'?'} → ${r.arrivalAirport||'?'}${r.flightDepartureTime?' · '+fmt12(r.flightDepartureTime):''}${r.passenger?' · '+r.passenger:''}`
                        : r.type==='hotel' ? `${r.startDate} to ${r.endDate}`
                        : r.type==='carservice' ? `${r.carServiceDriver||''} ${r.carServiceTime?'· '+r.carServiceTime:''}`.trim()
                        : r.type==='ground' ? r.driverPhone||''
                        : r.type==='car' ? r.carPickupLocation||''
                        : `${r.trainFrom||''} → ${r.trainTo||''}`;
                      const conf = r.hotelConfirmation||r.carConfirmation||r.trainConfirmation||r.flightConfirmation||r.carServiceConfirmation;
                      return (
                        <div key={r.id} onClick={() => setTravelModal(r)}
                          style={{ borderRadius:12, border:'1px solid var(--border)', cursor:'pointer', overflow:'hidden' }}>
                          {hero.imageUrl ? (
                            <div style={{ height:80, backgroundImage:`url(${hero.imageUrl})`, backgroundSize:'cover', backgroundPosition:'center', position:'relative' }}>
                              <div style={{ position:'absolute', inset:0, background:'linear-gradient(transparent 30%, var(--bg) 100%)' }} />
                              <div style={{ position:'absolute', bottom:8, left:12, display:'flex', alignItems:'center', gap:6 }}>
                                <i className={`fa-solid ${icons[r.type]}`} style={{ fontSize:11, color:'white' }}></i>
                                <span style={{ fontSize:11, fontWeight:700, color:'white', textShadow:'0 1px 3px rgba(0,0,0,0.5)' }}>{typeLabel}</span>
                              </div>
                            </div>
                          ) : (
                            <div style={{ height:48, background:hero.gradient, position:'relative', display:'flex', alignItems:'center', padding:'0 12px', gap:6 }}>
                              <i className={`fa-solid ${icons[r.type]}`} style={{ fontSize:11, color:'rgba(255,255,255,0.7)' }}></i>
                              <span style={{ fontSize:11, fontWeight:700, color:'rgba(255,255,255,0.8)' }}>{typeLabel}</span>
                            </div>
                          )}
                          <div style={{ padding:'10px 14px', background:'var(--bg)' }}>
                            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                              <p style={{ fontSize:14, fontWeight:700, color:'var(--text)', margin:0 }}>{name||r.type}</p>
                              {conf && <span style={{ fontSize:10, color:'var(--text3)', fontFamily:'monospace' }}>#{conf.slice(0,8)}</span>}
                            </div>
                            <p style={{ fontSize:12, color:'var(--text3)', margin:'2px 0 0' }}>{detail}</p>
                            {(r.greeterName||r.greeterPhone) && (
                              <p style={{ fontSize:11, color:'var(--text2)', marginTop:3 }}>
                                <i className="fa-solid fa-hand-wave" style={{ fontSize:9, color:'#fbbf24', marginRight:3 }}></i>
                                {r.greeterName||''}{r.greeterPhone?' · '+r.greeterPhone:''}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            </Card>
          ) : (
            <Card style={{ padding:40, textAlign:'center' }}>
              <i className="fa-regular fa-clipboard" style={{ fontSize:40, color:'var(--text3)', display:'block', marginBottom:12 }}></i>
              <p style={{ fontSize:16, fontWeight:700, color:'var(--text)', marginBottom:6 }}>No sheet for this date</p>
              <p style={{ fontSize:14, color:'var(--text2)', marginBottom:20 }}>Create a day sheet to get started</p>
              <Btn variant="primary" onClick={() => navigate(`/admin/sheets/new?artist=${selArtist||''}&date=${selDate}`)}>+ Create Day Sheet</Btn>
            </Card>
          )}
        </div>

      </div>
      <TravelModal record={travelModal} onClose={() => setTravelModal(null)} />

      {eventModal && (
        <EventModal
          event={eventModal.event}
          sheet={eventModal.sheet}
          prevEvent={eventModal.prevEvent}
          nextEvent={eventModal.nextEvent}
          colorMap={colorMap}
          onClose={() => setEventModal(null)}
          isAdmin
          onEventUpdated={(updated) => {
            setEventModal(null);
            setFullSheet(prev => prev ? { ...prev, events: prev.events.map(e => e.id === updated.id ? { ...e, ...updated } : e) } : prev);
          }}
        />
      )}

      {showSendModal && fullSheet && (
        <SendModal sheet={fullSheet} onClose={() => setShowSendModal(false)} onSent={(count) => {
          alert(`Email sent to ${count} recipient${count !== 1 ? 's' : ''}!`);
          setShowSendModal(false);
        }} />
      )}
    </div>
  );
}
